import { effect, inject, Injectable, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from '@openng/optimus-ui/api';
import { AuthService } from './auth.service';
import { LiveSessionRegistryService } from './live-session-registry.service';
import { PipService } from '../../shared/services/pip.service';

export const SESSION_EXPIRED_TOAST_KEY = 'session-expired';

/**
 * Finishes what the interceptor deferred: a session that could not be refreshed during a call is
 * left in place so the call survives, the user is told once, and the sign-out happens when the call
 * in this window ends.
 */
@Injectable({
  providedIn: 'root',
})
export class SessionExpiryService {
  private authService = inject(AuthService);
  private liveSessions = inject(LiveSessionRegistryService);
  private messageService = inject(MessageService);
  private pipService = inject(PipService);
  private router = inject(Router);

  constructor() {
    // A boolean signal only notifies when it flips, so every request that fails during the same call
    // lands here once. The overlay windows are too small for a toast and their styles are frozen; the
    // main window says it.
    effect(() => {
      const expired = this.authService.sessionExpiredDuringCall();
      untracked(() => {
        if (!expired) {
          this.messageService.clear(SESSION_EXPIRED_TOAST_KEY);
        } else if (this.pipService.isMaster()) {
          this.messageService.add({
            key: SESSION_EXPIRED_TOAST_KEY,
            severity: 'warn',
            summary: 'Session expired',
            detail: 'Your session expired. Your call will keep running. Please sign in again when it ends.',
            sticky: true,
          });
        }
      });
    });

    // Only the end of a call signs out, not "no call here": the overlay and reminder windows never
    // hold a call themselves and would otherwise sign out the moment they learned the session expired.
    let callWasRunning = false;
    effect(() => {
      const callRunning = this.liveSessions.isActiveHere();
      const callEnded = callWasRunning && !callRunning;
      callWasRunning = callRunning;
      if (!callEnded) return;
      untracked(() => {
        if (!this.authService.sessionExpiredDuringCall()) return;
        this.authService.logout();
        void this.router.navigate(['/login']);
      });
    });
  }
}
